// Pure payload-shaping helpers used by `useTrackEvent` and `trackEvent`
// in `use-track-event.js`. Extracted into a separate `.mjs` file so they
// can be exercised by `node:test` without React or a DOM, matching the
// pattern established by `tap-ref-capture.mjs`.

const BE_EVENT_NAME_REGEX = /^[A-Za-z0-9_\-:]{1,128}$/;
const TRACKED_COOKIE_KEYS = ["ttclid", "_ttp", "_fbc", "_fbp"];

/**
 * Codex review (site-frontend PR #1252, round 4, HIGH, addressed): a page
 * whose query string carries a bearer-style secret (e.g.
 * `/zakelijk/bevestigd`'s one-time WKR B2B confirm token -- holding it
 * lets anyone re-run a mutating backend action for that order) must never
 * have that secret reach Meta/TikTok. Redacting the `eventSourceUrl` this
 * module builds (see `buildTrackEventBody` below) only protects the
 * SERVER-SIDE CAPI dispatch -- it does nothing for the CLIENT-SIDE pixel
 * calls (`fbq('track', 'PageView')`, `ttq.page()`), which read
 * `window.location` internally with no URL-override parameter at all.
 * `firePixel` (`use-track-event.js`) therefore skips pixel-firing
 * ENTIRELY (not just URL-redacts) whenever the current path matches this
 * list, checked via `isTrackingSensitivePath`.
 *
 * `use-track-event.js`'s own `trackEvent()` imports this SAME list for
 * its `eventSourceUrl` redaction, so the two policies (skip the pixel;
 * redact the server URL) can never drift apart -- both live centrally in
 * ONE function, not per-caller. Independent review round 20 (LOW,
 * addressed): a previous version of this sentence named
 * `TrackingPageView.js` as the importer -- wrong, and contradicted by
 * that very component's own docblock, which explains why it deliberately
 * stopped doing any redaction of its own (round 4) and now just passes
 * `window.location.href` straight through to `trackEvent()`, trusting
 * the central fix one layer down.
 *
 * The pixel LOADER in `app/layout.js` (`window.__loadTrackingPixels`,
 * a raw inline `<script>` string that cannot `import` this module) also
 * duplicates this same prefix as a literal string check; keep it in
 * sync if this list ever changes. As of round 9, that loader does not
 * merely skip the PageView/detect EVENT calls on this path -- it skips
 * loading the Meta/TikTok/Tapfiliate SCRIPTS entirely (one early
 * `return` at the top of the function), because a loaded third-party
 * SDK is arbitrary code Favotrip does not control and can read
 * `window.location` on its own initialization regardless of which
 * explicit event calls this codebase happens to make. `app/layout.js`'s
 * `gtm` script duplicates the same check for the identical reason
 * (skip loading `gtm.js` entirely -- GTM never had a per-call opt-out
 * to begin with).
 *
 * `src/lib/monitoring/error-monitor.js` (round 8) imports THIS module
 * directly (it is not a raw inline script, so it can) to redact the
 * query string out of any error report's `url` field on this path --
 * a render-time client error here would otherwise ship the token to the
 * backend collector, which logs it server-side and forwards it to
 * Slack. `src/app/api/monitoring/error/route.js` imports it too, as a
 * second, server-side layer that does not trust the client-side
 * redaction alone.
 *
 * `src/middleware.js`'s `withReferrerPolicy` (round 16) imports this same
 * module too (a pure, edge-runtime-safe function -- no node/DOM deps),
 * to set a `Referrer-Policy: no-referrer` RESPONSE HEADER on this path --
 * the site-wide default policy otherwise sends the full current URL as
 * `Referer` on any same-origin request, a leak channel `page.js`'s own
 * `<meta name="referrer">` fix could not fully close (that tag only
 * governs requests started after Next.js gets around to emitting it, well
 * into `<head>` -- head-time script/font/image preloads and HTTP `Link:`
 * preload headers ignore it entirely; a real response header does not).
 *
 * Codex round 19 on PR #1252 (HIGH, addressed): this list used to be ONE
 * literal, NL-only path prefix. `middleware.js` calls
 * `isTrackingSensitivePath` against the PHYSICAL Dutch route
 * (`renderInternalPathname`), so it always correctly matched on every
 * market (a `.de` visit to the localized `/firmenkunden/bevestigd` slug
 * always got the Referrer-Policy header) -- but every OTHER consumer
 * above (`use-track-event.js`, `error-monitor.js`, `layout.js`'s inline
 * duplicates) calls it against `window.location.pathname`, the VISIBLE,
 * per-market URL. On `.de`/`.fr`/`.co.uk`/`.pl` those checks missed
 * entirely, so `use-track-event.js`'s `/api/track/event` body still
 * carried the raw confirm token unredacted on those domains (verified
 * live by independent review round 18, which flagged it non-blocking;
 * Codex round 19, reviewing the same fact independently, treated it as
 * blocking -- fixed rather than argued, since a real fix was already
 * scoped and available).
 *
 * Fixed by listing every market's localized slug for this ONE route,
 * not just the Dutch one, so every consumer of this shared list --
 * client-side guards included, not just `middleware.js` -- matches on
 * every TLD. `zakelijk`'s localized segments come from
 * `src/lib/slug-paths.js`'s `SLUG_SEGMENT_I18N.zakelijk` (the site's
 * single source of truth for this mapping); NOT imported directly
 * (this module is deliberately dependency-free -- edge-runtime-safe AND
 * exercised by plain `node --test`, both of which resolving a second,
 * heavier module correctly is an avoidable risk for one small, stable,
 * business-name slug that changes rarely). `slug-paths.test.js` already
 * guards `SLUG_SEGMENT_I18N` itself against drift; the
 * `TRACKING_SENSITIVE_PATH_PREFIXES` pin test below guards THIS copy --
 * if either ever changes without the other, one of the two test suites
 * fails, which is the intended tripwire.
 */
export const TRACKING_SENSITIVE_PATH_PREFIXES = [
  "/zakelijk/bevestigd", // nl (and the physical route for every market)
  "/firmenkunden/bevestigd", // de -- slug-paths.js SLUG_SEGMENT_I18N.zakelijk.de
  "/entreprises/bevestigd", // fr -- .zakelijk.fr
  "/business/bevestigd", // en (.co.uk) -- .zakelijk.en
  "/dla-firm/bevestigd", // pl -- .zakelijk.pl
  // Masterplan 2026-08-30 (audit finding H3): the Stripe success_url
  // thank-you page, same bearer-style-secret-in-the-URL shape as
  // /zakelijk/bevestigd above (a signed order id+token pair).
  //
  // Codex review (PR #1282, round 4, HIGH, addressed -- correcting a false
  // "NL-only, no localized slug exists yet" assumption in an earlier
  // version of this comment): `mapManagedSegments`/`localizePath`
  // (slug-paths.js) translate ONLY segment[0] of a path -- "zakelijk" is
  // NOT in `LOCALIZABLE_CONTAINERS`, so segment[1] ("bedankt") is NEVER
  // translated to a different word, it always passes through verbatim.
  // That means this route is ALREADY reachable on every market the moment
  // it exists, with NO separate registration needed: /firmenkunden/bedankt
  // (de), /entreprises/bedankt (fr), /business/bedankt (en/.co.uk),
  // /dla-firm/bedankt (pl) -- confirmed read-only against slug-paths.js's
  // own `SLUG_SEGMENT_I18N.zakelijk` mapping, the exact same mechanism
  // that makes the four /bevestigd siblings above reachable.
  "/zakelijk/bedankt",
  "/firmenkunden/bedankt",
  "/entreprises/bedankt",
  "/business/bedankt",
  "/dla-firm/bedankt",
  // The DOI confirmation token lives in the URL fragment on this route.
  // Fragments are client-only, so middleware alone cannot protect them:
  // every client-side tracker must treat the visible localized path as
  // sensitive before the confirmation component gets a chance to scrub it.
  "/zakelijk/inschrijven",
  "/firmenkunden/inschrijven",
  "/entreprises/inschrijven",
  "/business/inschrijven",
  "/dla-firm/inschrijven",
  // WP6c: the voucher-redeem page will carry the vouchercode in the URL
  // fragment (built in a separate PR). Same "fragment is client-only, so
  // middleware alone cannot protect it" reasoning as /inschrijven above --
  // every client-side tracker must treat the visible path as sensitive.
  // Unlike /zakelijk/*, the redeem route is MARKET-keyed, not
  // locale-keyed (fr-be and fr-fr share the French slug) -- the five
  // entries below are every value of REDEEM_SLUG_BY_MARKET /
  // ALL_REDEEM_SLUGS in src/lib/redeem-paths.js, the source of truth for
  // this mapping (not imported, same dependency-free reasoning as the
  // zakelijk slugs above).
  "/verzilveren", // nl-nl, nl-be
  "/einloesen", // de-de
  "/echanger", // fr-be, fr-fr
  "/redeem", // en-gb
  "/wymien", // pl-pl
  // Codex review (site-frontend PR #1450, addressed): /zakelijk/beheer/
  // [token] carries the B2B order-manage token as a PATH SEGMENT (not a
  // query string or fragment like every route above) -- that token grants
  // access to the recipient list, lets someone CHANGE it, and downloads
  // the voucher codes CSV. Same "zakelijk is not in LOCALIZABLE_CONTAINERS,
  // so segment[1] is never translated" mechanism as /bevestigd, /bedankt
  // and /inschrijven above -- reachable verbatim on every market. Adding
  // the prefix here is necessary but NOT sufficient on its own -- see
  // TRACKING_PATH_SEGMENT_SECRET_PREFIXES + safeTrackingPathname below.
  "/zakelijk/beheer",
  "/firmenkunden/beheer",
  "/entreprises/beheer",
  "/business/beheer",
  "/dla-firm/beheer",
  // ING-presentatie (23-10-2026): de toegangscode wordt in de browser
  // getypt en ontsleutelt de inhoud. Geen enkele tracker mag op deze pagina
  // laden of de invoer kunnen zien.
  "/ing/presentation",
];

/**
 * Codex review (site-frontend PR #1450, addressed): every consumer that
 * builds a "safe" URL for a sensitive path (`use-track-event.js`'s
 * `eventSourceUrl`, `error-monitor.js`/`api/monitoring/error/route.js`'s
 * redaction) builds `${origin}${pathname}` -- the RAW pathname, WITH any
 * segments after the matched prefix kept (deliberately: for every route
 * above, the secret lives in the query string or URL fragment, not the
 * pathname, and a sub-path is still a real, useful page to report --
 * `error-monitor.test.js` pins exactly this: a client error on
 * `/zakelijk/bevestigd/extra` still reports the FULL
 * `/zakelijk/bevestigd/extra` pathname, not truncated to the bare
 * prefix). `/zakelijk/beheer/[token]` breaks that assumption: the
 * segment AFTER the prefix (the sub-path itself) IS the secret, so
 * keeping it is exactly the leak. This is the subset of
 * TRACKING_SENSITIVE_PATH_PREFIXES where that holds -- every other
 * consumer must special-case these when building a "safe" URL, which is
 * exactly what `safeTrackingPathname()` below does centrally so no
 * caller has to remember it individually.
 */
export const TRACKING_PATH_SEGMENT_SECRET_PREFIXES = [
  "/zakelijk/beheer",
  "/firmenkunden/beheer",
  "/entreprises/beheer",
  "/business/beheer",
  "/dla-firm/beheer",
];

/**
 * @param {string} pathname
 * @returns {string | null} the matched TRACKING_SENSITIVE_PATH_PREFIXES
 *   entry, or null if `pathname` isn't sensitive at all.
 */
export function matchedSensitiveTrackingPrefix(pathname) {
  const p = typeof pathname === "string" ? pathname : "";
  return (
    TRACKING_SENSITIVE_PATH_PREFIXES.find(
      (prefix) => p === prefix || p.startsWith(`${prefix}/`),
    ) ?? null
  );
}

/** @param {string} pathname */
export function isTrackingSensitivePath(pathname) {
  return matchedSensitiveTrackingPrefix(pathname) !== null;
}

/**
 * The pathname a caller should actually put into a "safe" URL for
 * tracking/error-reporting purposes: `null` when `pathname` isn't
 * sensitive at all (caller keeps using the real URL); the matched
 * PREFIX (not the raw pathname) when the sensitive value is itself a
 * path segment (`TRACKING_PATH_SEGMENT_SECRET_PREFIXES` -- today just
 * `/zakelijk/beheer` and its localized siblings); the raw `pathname`
 * unchanged for every other sensitive route, preserving the existing,
 * already-tested behaviour (a sub-path of e.g. `/zakelijk/bevestigd` is
 * still a real page worth reporting -- only ITS query string/fragment is
 * the secret, already handled by the caller never appending one).
 *
 * @param {string} pathname
 * @returns {string | null}
 */
export function safeTrackingPathname(pathname) {
  const prefix = matchedSensitiveTrackingPrefix(pathname);
  if (prefix === null) return null;
  return TRACKING_PATH_SEGMENT_SECRET_PREFIXES.includes(prefix)
    ? prefix
    : pathname;
}

/**
 * Build a deduplication id with the form `evt_<lowername>_<random>`.
 * Falls back to a timestamp-based id if `crypto.randomUUID` is absent.
 *
 * @param {string} eventName
 * @param {{ randomUUID?: () => string }} [cryptoLike]
 * @returns {string}
 */
export function generateEventId(eventName, cryptoLike) {
  const name = String(eventName || "evt").toLowerCase();
  let uuid = null;
  try {
    const c = cryptoLike ?? (typeof crypto !== "undefined" ? crypto : null);
    if (c && typeof c.randomUUID === "function") {
      uuid = c.randomUUID();
    }
  } catch {
    uuid = null;
  }
  if (uuid) return `evt_${name}_${uuid}`;
  const ts = Date.now();
  const rand = Math.random().toString(36).slice(2, 10);
  return `evt_${name}_${ts}_${rand}`;
}

/**
 * Read marketing-consent state from a storage-like object (browser
 * `window.localStorage` or a Map-wrapping shim in tests). Mirrors the
 * same precedence as `Providers`: granular prefs > legacy sentinel.
 *
 * @param {Pick<Storage, "getItem">} storage
 * @returns {boolean}
 */
export function readMarketingConsent(storage) {
  if (!storage || typeof storage.getItem !== "function") return false;
  try {
    const raw = storage.getItem("cookie_consent_prefs");
    if (raw) {
      try {
        const prefs = JSON.parse(raw);
        return prefs?.marketing === true;
      } catch {
        return false;
      }
    }
    return storage.getItem("cookie_consent") === "accepted";
  } catch {
    return false;
  }
}

/**
 * Parse `Cookie` header / `document.cookie` string and return the value
 * for the named key, or `undefined` when absent.
 *
 * @param {string} cookieString
 * @param {string} name
 * @returns {string|undefined}
 */
export function readCookieFrom(cookieString, name) {
  if (!cookieString || typeof cookieString !== "string") return undefined;
  const safeName = String(name).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const match = cookieString.match(
    new RegExp(`(?:^|;\\s*)${safeName}=([^;]+)`),
  );
  if (!match) return undefined;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

/**
 * Build the `POST /api/track/event` request body. Strips any field
 * whose value is `undefined` / `null` so the BE DTO whitelist
 * (`forbidNonWhitelisted: true`) does not reject the request, and
 * forwards the tracked cookies + consent flag.
 *
 * @param {object} params
 * @param {'Purchase'|'ViewContent'|'InitiateCheckout'|'PageView'} params.eventName
 * @param {string} params.eventId
 * @param {string} [params.eventSourceUrl]
 * @param {number} [params.amount]
 * @param {string} [params.currency]
 * @param {string|number} [params.productId]
 * @param {string} [params.productName]
 * @param {string} [params.email]
 * @param {string} [params.phone]
 * @param {string} [params.cookieString]
 * @param {boolean} [params.consent]
 * @returns {Record<string, unknown>}
 */
export function buildTrackEventBody({
  eventName,
  eventId,
  eventSourceUrl,
  amount,
  currency,
  productId,
  productName,
  email,
  phone,
  cookieString,
  consent,
  clientId,
  emailHash,
}) {
  const cookies = {};
  if (cookieString) {
    for (const key of TRACKED_COOKIE_KEYS) {
      const v = readCookieFrom(cookieString, key);
      if (v !== undefined) cookies[key] = v;
    }
  }
  const body = {
    eventName,
    eventId,
    eventSourceUrl,
    amount,
    currency,
    productId: productId !== undefined ? String(productId) : undefined,
    productName,
    email,
    phone,
    ttclid: cookies.ttclid,
    ttp: cookies._ttp,
    fbc: cookies._fbc,
    fbp: cookies._fbp,
    consent,
    // P1a 2026-05-15 — Stable cross-event identity. Backend uses it
    // as Meta + TikTok `external_id` (with fallback to `eventId` for
    // backwards compatibility on the moment of deploy).
    clientId,
    // P1b 2026-05-15 — Pre-hashed email signal. Browser hashes the
    // address with SHA-256 (lowercased + trimmed) the moment a form
    // captures it; we forward the 64-hex string and the BE plugs it
    // directly into `user_data.em` (Meta) / `user.email` (TikTok)
    // without re-hashing. Plaintext never crosses the wire.
    emailHash,
  };
  return Object.fromEntries(
    Object.entries(body).filter(([, v]) => v !== undefined && v !== null),
  );
}

/**
 * BE eventId regex — kept in sync with `track-event.dto.ts`. Exposed so
 * tests + callers can pre-validate before posting.
 */
export function isValidEventId(value) {
  return typeof value === "string" && BE_EVENT_NAME_REGEX.test(value);
}
